export const formatCurrency = (amount: string | number | undefined | null): string => {
  if (amount == null) {
    return '£0';
  }
  if (typeof amount === 'string') {
    amount = parseFloat(amount);
  }

  const isWholeNumber = amount % 1 === 0;
  return `£${amount.toLocaleString('en-GB', {
    minimumFractionDigits: isWholeNumber ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

export const calculateAnnualizedTotal = <T extends Record<string, unknown>>(
  items: T[] | undefined,
  frequencyField = 'frequency',
  amountField = 'amount',
): number => {
  if (!items || items.length === 0) return 0;

  return items.reduce((total, item) => {
    const amount = typeof item[amountField] === 'number' ? (item[amountField] as number) : 0;
    const frequency = item[frequencyField] as string | undefined;
    return total + calculateAnnualAmount(amount, frequency);
  }, 0);
};

export function calculateAnnualAmount(amount: number | undefined, frequency?: string): number {
  if (!amount) {
    return 0;
  }
  switch (frequency) {
    case 'Weekly':
      return amount * 52;
    case 'Monthly':
      return amount * 12;
    case 'Annually':
      return amount;
    case 'One-off':
      return amount;
    default:
      return amount;
  }
}

export function calculateMonthlyAmount(amount: number, frequency?: string): number {
  return calculateAnnualAmount(amount, frequency) / 12;
}

export type Frequency = 'monthly' | 'annually';
export function useTotalForFrequency<T extends Record<string, unknown>>({
  items,
  frequency,
  frequencyField,
  amountField,
}: {
  items?: T[];
  frequency: Frequency;
  frequencyField: string;
  amountField: string;
}): number {
  if (!items || items.length === 0) return 0;

  const annualAmount = calculateAnnualizedTotal(items, frequencyField, amountField);

  if (frequency === 'monthly') {
    return annualAmount / 12;
  }
  return annualAmount;
}
